import {observer} from 'mobx-react-lite'
import {Suspense, useEffect, useState} from 'react'
import React from 'react'
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom'
import {Lang} from 'yougile-lang'
import {logger} from 'yougile-util'
import {googleCalendarRedirectPage} from 'yougile-model'
import {Notifier} from '@api/model/notifier'
import {LoaderIcon} from '@components/common/base/loader-new'
import {ConfirmManager} from '@components/common/base/notifications/show-confirm'
import {ErrorBoundary} from '@components/common/error-boundary'
import {EmptyPage} from '@components/logged-in/empty-page'
import {NoLicense} from '@components/no-license'
import {rightPanelStore} from '@components/right-panel/right-panel'
import {useGlobalKeyDown} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {idTasksApi} from '@extensions/id-tasks/id-tasks-store'
import {PageService} from '@extensions/page-service'
import {RootContext, rootStore, useRootStore} from './root-store'

export const Root = () => (
  <ErrorBoundary
    silent
    onError={e => {
      logger.error('root error ' + e.message)
    }}
  >
    <RootContext.Provider value={rootStore}>
      <RootBase />
    </RootContext.Provider>
  </ErrorBoundary>
)

const useUrlPaste = (): void => {
  const {pathname} = useLocation()
  const navigate = useNavigate()

  useGlobalKeyDown({
    anyKey: async ({alt, code}) => {
      let hasError = false
      if (alt && code === 'KeyV') {
        const clipboardData = decodeURI(await navigator.clipboard.readText())
        const isLink = clipboardData.includes('://')
        if (!isLink) {
          hasError = true
        }
        const isYougileLink = clipboardData.includes('/team')
        if (isYougileLink) {
          const hash = clipboardData.split('#')[1]
          if (hash) {
            const taskId = await idTasksApi.getHub(hash)
            if (!taskId) {
              Notifier.warn(Lang.msg.noTaskByHashInClipboard + hash)
              return
            }
            navigate(`${pathname}#${hash}`)
            if (!rightPanelStore.state.chatPanel.opened) {
              rightPanelStore.chatPanelToggle()
            }
          } else {
            hasError = true
          }
        } else {
          hasError = true
        }
        if (hasError) {
          if (clipboardData.length > 0) {
            Notifier.warn(
              Lang.msg.noLinkInClipboard +
                (clipboardData.length > 100
                  ? clipboardData.slice(0, 100) + '...'
                  : clipboardData)
            )
          } else {
            Notifier.warn(Lang.msg.nothingInClipboard)
          }
        }
      }
    }
  })
}
const RootBase = observer(() => {
  const [state] = useRootStore()
  const [body] = useState(() => document.getElementById('body'))

  useEffect(() => {
    state.loggedIn && body?.classList.add('logged-in')
    return () => body?.classList.remove('logged-in')
  }, [state.loggedIn])

  useEffect(() => {
    globals.companyChanging && body?.classList.add('changing')
    return () => body?.classList.remove('changing')
  }, [globals.companyChanging])

  return (
    <>
      {(!state.loaded || globals.companyChanging) && <LoaderIcon />}
      <ConfirmManager />

      {state.loaded && (
        <Suspense fallback={<LoaderIcon />}>
          <Router>
            <Routes>
              <Route path="/*" element={<RootLoaded />} />
              <Route path="/register" element={<Register />} />
              <Route path="/mail-settings" element={<MailSettingsPage />} />
              <Route path="/recovery/*" element={<Recovery />} />
              <Route path="/email-change" element={<EmailChangePage />} />

              <Route path="/team/register" element={<Register />} />
              <Route
                path="/team/mail-settings"
                element={<MailSettingsPage />}
              />
              <Route path="/team/recovery/*" element={<Recovery />} />
              <Route path="/team/email-change" element={<EmailChangePage />} />
            </Routes>
          </Router>
        </Suspense>
      )}
    </>
  )
})

const RootLoaded = observer(() => {
  const [state] = useRootStore()

  const navigate = useNavigate()
  useEffect(() => {
    PageService.navigationInit(navigate)
  }, [])
  useUrlPaste()

  // TODO each render location is new
  const location = useLocation()
  useEffect(() => void PageService.up(), [location])

  // TODO! check state.page inn other way
  return (
    <>
      {state.page === 'login' && <Login />}
      {state.page === 'company-login' && <CompanyLogin />}
      {state.page === 'no-users' && <StandaloneWelcome />}
      {state.page === 'no-license' && <NoLicense />}
      {state.page === googleCalendarRedirectPage && <EmptyPage />}
      {state.loggedIn && state.page !== googleCalendarRedirectPage && (
        <LoggedIn />
      )}
    </>
  )
})

const Register = React.lazy(() => import('../register'))
const MailSettingsPage = React.lazy(() => import('../mail-settings-page'))
const Recovery = React.lazy(() => import('../recovery'))
const Login = React.lazy(() => import('../login'))
const CompanyLogin = React.lazy(() => import('../company-login'))
const StandaloneWelcome = React.lazy(() => import('../standalone-welcome'))
const EmailChangePage = React.lazy(() => import('../email-change-page'))
const LoggedIn = React.lazy(() => import('../logged-in'))
